import './styles.scss';

import {
  Children,
  ForwardedRef,
  KeyboardEvent,
  MouseEvent,
  ReactElement,
  forwardRef,
  isValidElement,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import PopoverInput from '@/common/PopoverInput';
import SelectList from '@/common/Select/SelectList';
import SelectOption from '@/common/Select/SelectOption';
import { SelectTypes } from '@/common/Select/types';
import { getClasses } from '@/utils';
import { getTextFromChildren } from '@/common/Select/utils';
import useForwardRef from '@/hooks/useForwardRef';

const Select = forwardRef<SelectTypes.RefType, SelectTypes.Props>(
  ({ children, multiple, ...props }: SelectTypes.Props, ref: ForwardedRef<SelectTypes.RefType>): ReactElement => {
    const inputRef = useForwardRef(ref);
    const [filter, setFilter] = useState<string>('');
    const [focus, setFocus] = useState<boolean>(false);
    const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
    const [maxHeight, setMaxHeight] = useState<number | undefined>(undefined);
    const optionsListRef = useRef<HTMLDivElement>(null);

    const isMulti = multiple !== undefined && multiple !== false;

    const handleShow = (): void => {
      setFocus(true);
      setHighlightedIndex(-1);
      setTimeout(() => {
        const input = inputRef.current?.target || inputRef.current;
        if (input) {
          const rect = input.getBoundingClientRect();
          const viewportHeight = window.innerHeight;
          const spaceBelow = viewportHeight - rect.bottom;
          const margin = 16;
          let available = spaceBelow - margin;
          setMaxHeight(available > 100 ? available : 100);
        } else {
          setMaxHeight(undefined);
        }
      }, 0);
    };
    const handleHide = (): void => {
      setFocus(false);
      setFilter('');
      setHighlightedIndex(-1);
    };

    const handleInputChange = (input: string) => {
      setFilter(input);
      setHighlightedIndex(0);
    };
    const handleSelect = (newValue: SelectTypes.ValueProp, event?: MouseEvent<Element, globalThis.MouseEvent> | KeyboardEvent<Element>) => {
      if (!isMulti) inputRef.current?.blur?.();
      const selectedValue: string | number = Array.isArray(newValue) ? newValue.map(String).join(',') : newValue;
      props.onChange?.(selectedValue, event);
      if (!isMulti) handleHide();
    };
    const handleTab = (v, e) => {
      setFilter('');
      props.onTab?.(v, e);
    };

    const handleKeyDown = (_value: string, event: KeyboardEvent<HTMLInputElement>) => {
      if (!focus) return;
      if (options.length === 0) return;
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setHighlightedIndex((prev) => {
          const next = prev < options.length - 1 ? prev + 1 : 0;
          let idx = next;
          let tries = 0;
          while (options[idx]?.disabled && tries < options.length) {
            idx = (idx + 1) % options.length;
            tries++;
          }
          return idx;
        });
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setHighlightedIndex((prev) => {
          const next = prev > 0 ? prev - 1 : options.length - 1;
          let idx = next;
          let tries = 0;
          while (options[idx]?.disabled && tries < options.length) {
            idx = (idx - 1 + options.length) % options.length;
            tries++;
          }
          return idx;
        });
      } else if (event.key === 'Enter') {
        if (highlightedIndex >= 0 && highlightedIndex < options.length) {
          const option = options[highlightedIndex];
          if (!option.disabled) {
            handleSelect(option.value, event);
          }
        }
      }
    };

    const filterText = focus ? filter : '';

    const validChildren = useMemo(
      () =>
        Children.toArray(children).filter((child) => {
          if (!isValidElement(child)) return false;
          const childText = getTextFromChildren(child.props.children);
          if (!filterText) return true;
          return childText.toLowerCase().includes(filterText.toLowerCase());
        }),
      [children, filterText]
    );
    const options = useMemo(() => {
      if (validChildren.length === 0) return [{ label: 'No results found', value: null, disabled: true }];
      return validChildren.map((child: ReactElement) => {
        const { value: propsValue, children: propsChildren, ...childProps } = child.props;
        const childValue = Array.isArray(propsValue) ? propsValue.map(String) : (propsValue || '').toString();
        const childText = propsChildren;
        return {
          ...childProps,
          label: childText,
          value: childValue,
          disabled: childProps.disabled !== undefined && childProps.disabled !== false,
        };
      });
    }, [validChildren]);

    useEffect(() => {
      let selectedIndex = -1;
      const currentValue = props.value;
      if (Array.isArray(currentValue)) {
        selectedIndex = options.findIndex((opt) => currentValue.map(String).includes(String(opt.value)));
      } else {
        selectedIndex = options.findIndex((opt) => String(opt.value) === String(currentValue));
      }
      if (selectedIndex === highlightedIndex) return;
      setHighlightedIndex(filter ? 0 : selectedIndex);
      /* Ignore highlightedIndex
        Negates the change/effect.
      */ // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.value, options, focus, filter]);
    useEffect(() => {
      optionsListRef.current?.querySelector('.highlighted')?.scrollIntoView?.({ block: 'nearest' });
    }, [highlightedIndex]);

    let selectedLabel = '';
    const currentValues = Array.isArray(props.value) ? props.value.map(String) : String(props.value).split(',');
    const selectedGroups = options.filter(
      (opt) => Array.isArray(opt.value) && opt.value.map(String).every((value) => currentValues.includes(value))
    );
    const groupedValues = new Set(selectedGroups.flatMap((opt) => opt.value.map(String)));
    const selectedOptions = options
      .filter((opt) => {
        if (opt.value == null) return false;
        const optionValue = Array.isArray(opt.value) ? opt.value : opt.value.split(',');
        const isSelected = optionValue.map(String).every((val) => currentValues.includes(val));
        return isSelected && (Array.isArray(opt.value) || !optionValue.some((value) => groupedValues.has(String(value))));
      })
      .map((opt) => getTextFromChildren(opt.label));
    selectedLabel = [...new Set(selectedOptions)].join(', ');
    const inputValue = focus ? filter : selectedLabel;
    const inputPlaceholder = focus ? selectedLabel || props.placeholder || 'Select...' : props.placeholder || 'Select...';

    return (
      <PopoverInput
        {...props}
        value={inputValue}
        placeholder={inputPlaceholder}
        className={getClasses('Select', 'Common', props.className)}
        onChange={handleInputChange}
        onTab={handleTab}
        onShow={handleShow}
        onHide={handleHide}
        onKeyDown={handleKeyDown}
        hideOnEnter={!isMulti}
        ref={inputRef}
      >
        <SelectList
          style={{
            minWidth: inputRef.current ? inputRef.current.target.offsetWidth : 'auto',
            maxHeight: maxHeight ? maxHeight : undefined,
            overflowY: maxHeight ? 'auto' : undefined,
          }}
          ref={optionsListRef}
        >
          {options.map((option, o: number): ReactElement => {
            const currentValue = props.value;
            const currentValues = Array.isArray(currentValue) ? currentValue.map(String) : String(currentValue).split(',');
            const optionValue = Array.isArray(option.value) ? option.value.join(',') : String(option.value);
            const optionValues = optionValue.split(',').map((value) => value.trim());
            const isActive = !option.disabled && optionValues.every((value) => currentValues.includes(value));
            return (
              <SelectOption
                {...option}
                active={isActive}
                className={getClasses(option.className, o === highlightedIndex && !option.disabled && 'highlighted')}
                onClick={(event) => handleSelect(optionValue, event)}
                disabled={option.disabled}
                key={o}
              >
                {option.label}
              </SelectOption>
            );
          })}
        </SelectList>
      </PopoverInput>
    );
  }
) as SelectTypes.Component;
Select.displayName = 'Select';

Select['Option'] = SelectOption;
Select['List'] = SelectList;

export default Select;
